import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Clock } from "lucide-react";
import React, { useState } from "react";
import { useBillingSubscription } from "@/hooks/use-billing-subscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function SidebarTrialProgressComponent() {
  const { displayRules, trialInfo } = useBillingSubscription();
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Use centralized logic for display rules and trial info
  const shouldShowUpgrade = displayRules.shouldShowUpgradePrompts && trialInfo.isActive;
  const { daysRemaining, progressPercentage } = trialInfo;

  const handleUpgrade = async () => {
    if (isUpgrading) return;
    
    setIsUpgrading(true);
    try {
      // Import and use the centralized checkout hook functionality
      const { getRefgrowReferralCode } = await import('@/lib/refgrow');
      const referralCode = getRefgrowReferralCode();
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId: 'pro-monthly',
          referralCode
        }
      });
      
      if (error) {
        console.error('Error creating checkout:', error);
        toast.error('Failed to create checkout session');
        return;
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create checkout session');
    } finally {
      setIsUpgrading(false);
    }
  };

  // Don't show if we shouldn't show upgrade
  if (!shouldShowUpgrade) {
    return null;
  }

  return (
    <Card className="mx-2 my-2 border border-black/10 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <CardContent className="p-2">
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-1 mb-2">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">
              {daysRemaining} days left
            </span>
          </div>
          <Progress 
            value={progressPercentage} 
            className="h-1.5 mb-1.5 w-full bg-red-500/10 max-w-[130px] [&>div]:bg-red-500"
          />
        </div>
        <div className="flex justify-center">
          <Button 
            onClick={() => handleUpgrade()}
            disabled={isUpgrading}
            size="sm"
            className="w-fit bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border-0"
          >
            <Sparkles className="h-3 w-3 mr-0" />
            {isUpgrading ? 'Loading...' : 'Upgrade to Pro'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Export memoized component to prevent unnecessary re-renders
export const SidebarTrialProgress = React.memo(SidebarTrialProgressComponent);